import {useState} from 'react'
import {AxiosError} from 'axios'
import {MenuButton, MenuProvider, useDialogStore} from '@ariakit/react'
import {Button} from '@/components/ui/button/Button'
import {CheckIcon, DotsHorizontalIcon, Download01Icon, RefreshCw01Icon, Trash01Icon} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {DeleteDialog} from '@/components/commons/delete-dialog/DeleteDialog'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {formatLocaleDate} from '@/dayjs'
import {buildFile, capitalize, downloadFile, errorHandler} from '@/utilities/helpers'
import {httpDownloadExport} from '@/features/exports/http/exports.http'
import {ActivityReport, ActivityReportError, ActivityReportExportJob} from '@/features/accesses/activity-report/types'
import {useRegenerateActivityReport} from '@/features/accesses/activity-report/queries/useRegenerateActivityReport'
import {useBillActivityReport} from '@/features/accesses/activity-report/queries/useBillActivityReport'
import {useDeleteActivityReport} from '@/features/accesses/activity-report/queries/useDeleteActivityReport'
import {ConfirmActivityReportActionDialog} from '@/features/accesses/activity-report/components/confirm-activity-report-action-dialog/ConfirmActivityReportActionDialog'
import {
    activityReportSectorToLabel,
    canDownloadActivityReportFile,
    canRegenerateActivityReportFile,
    resolveActivityReportErrorLabel
} from '@/features/accesses/activity-report/utils'

// Download the ready file. Prefers the signed `downloadUrl`; otherwise streams the blob from the
// shared export-job download endpoint. Mirrors `downloadExportJob` (@/features/exports) but accepts
// the thinner embedded job shape carried by an activity report.
const downloadActivityReportFile = async (job: ActivityReportExportJob): Promise<void> => {
    if (job.downloadUrl) {
        const link = document.createElement('a')
        link.href = job.downloadUrl
        link.target = '_blank'
        link.rel = 'noopener'
        if (job.fileName) link.download = job.fileName
        link.click()
        return
    }

    const response = await httpDownloadExport(job.id)
    const baseName = job.fileName?.replace(/\.[^./\\]+$/, '') || `rendiconto-${job.id}`
    downloadFile(buildFile({extension: 'xlsx', fileName: baseName, data: response.data}))
}

type ActivityReportRowMenuProps = {
    report: ActivityReport
}

export const ActivityReportRowMenu = ({report}: ActivityReportRowMenuProps) => {
    const {t} = useTranslation()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [isBillConfirmOpen, setIsBillConfirmOpen] = useState(false)
    const deleteDialog = useDialogStore()

    const exportJob = report.exportJob
    const canDownload = canDownloadActivityReportFile(exportJob?.status)
    const canRegenerate = canRegenerateActivityReportFile(exportJob?.status)
    const isReportPending = report.status === 'pending'

    const onMutationError = (error: AxiosError<ActivityReportError>) =>
        toast.error(t(resolveActivityReportErrorLabel(error.response?.data?.code)))

    const regenerateMutation = useRegenerateActivityReport({
        onSuccess: () => toast.success(t('activity_report:regenerate_success')),
        onError: onMutationError
    })

    const billMutation = useBillActivityReport({
        onSuccess: () => {
            toast.success(t('activity_report:bill_success'))
            setIsBillConfirmOpen(false)
        },
        onError: onMutationError
    })

    const deleteMutation = useDeleteActivityReport(report.id, {
        onSuccess: () => {
            toast.success(t('activity_report:delete_success'))
            deleteDialog.hide()
        },
        onError: error => {
            onMutationError(error)
            deleteDialog.hide()
        }
    })

    const handleDownload = async () => {
        if (!exportJob) return
        setIsDownloading(true)
        try {
            await downloadActivityReportFile(exportJob)
        } catch (error) {
            errorHandler(error)
        } finally {
            setIsDownloading(false)
        }
    }

    const reportName = `${capitalize(formatLocaleDate(report.startDate, 'MMMM YYYY'))} · ${t(
        activityReportSectorToLabel[report.sector]
    )}`

    return (
        <>
            <MenuProvider open={isMenuOpen} setOpen={setIsMenuOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isMenuOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="240px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        {canDownload && (
                            <MenuItem onClick={handleDownload} disabled={isDownloading}>
                                <Download01Icon />
                                {t('activity_report:download')}
                                {isDownloading && <Spinner size={12} />}
                            </MenuItem>
                        )}
                        {canRegenerate && (
                            <MenuItem
                                onClick={() => regenerateMutation.mutate({id: report.id})}
                                disabled={regenerateMutation.isPending}
                            >
                                <RefreshCw01Icon />
                                {t('activity_report:regenerate')}
                                {regenerateMutation.isPending && <Spinner size={12} />}
                            </MenuItem>
                        )}
                        {isReportPending && (
                            <>
                                <MenuItem onClick={() => setIsBillConfirmOpen(true)}>
                                    <CheckIcon />
                                    {t('activity_report:mark_as_billed')}
                                </MenuItem>
                                <MenuItem onClick={() => deleteDialog.show()}>
                                    <Trash01Icon />
                                    {t('activity_report:delete')}
                                </MenuItem>
                            </>
                        )}
                    </MenuGroup>
                </Menu>
            </MenuProvider>

            {isBillConfirmOpen && (
                <ConfirmActivityReportActionDialog
                    isOpen
                    onClose={() => setIsBillConfirmOpen(false)}
                    title="activity_report:bill_confirm_title"
                    description="activity_report:bill_confirm_description"
                    confirmLabel="activity_report:bill_confirm_cta"
                    isPending={billMutation.isPending}
                    onConfirm={() => billMutation.mutate({id: report.id})}
                />
            )}

            <DeleteDialog
                dialogStore={deleteDialog}
                deleteMutation={deleteMutation}
                canBeDeleted={isReportPending}
                title={t('activity_report:delete_confirm_title')}
                paragraph1={t('commons:delete_paragraph_1')}
                targetNameToDelete={reportName}
                deniedTitle={t('activity_report:delete_confirm_title')}
                deniedParagraph={t('activity_report:error_already_billed')}
            />
        </>
    )
}
